import { lazy, Suspense } from "react";
import type { ComponentType } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// Eager: the common first-paint entry points stay in the initial chunk.
import { SignIn } from "./routes/SignIn.js";
import { Home } from "./routes/Home.js";
import { Marketing } from "./routes/Marketing.js";
import { flag } from "./lib/flags.js";
import Authorize from "./routes/Authorize.js";
import { claimChunkReload, loadWithRetry } from "./lib/chunk-reload.js";
// Lazy: every other route splits into its own chunk, so landing on /, /signin,
// or /app/home no longer ships all six drills + the practice-test runner +
// Desmos + KaTeX up front. (Routes export named components, hence the .then map.)
/**
 * Lazy-load a route's NAMED export, surviving a deploy that lands while a tab is
 * already open.
 *
 * WHY. These routes export named components, so the module has to be mapped to a
 * default. When a deploy replaces the hashed chunks this shell was built against,
 * the old chunk is gone, the module resolves without the export, and the map
 * reads a property of undefined: "Cannot read properties of undefined (reading
 * 'MistakeReview')". Production logged that shape 12 times across six routes,
 * both browser engines, desktop and mobile, every one a signed-in user losing the
 * page they had asked for.
 *
 * Reloading picks up the new index.html and its current chunk hashes. The guard
 * in lib/chunk-reload.ts allows one reload PER BUILD, so a genuinely missing
 * export raises instead of reloading forever, and a later deploy still gets a
 * fresh attempt. It used to be one reload per route for the life of the tab,
 * which left a student's score report permanently unreachable after a second
 * deploy landed; see that file for the production trace.
 *
 * A DEPLOY IS NOT THE ONLY WAY AN IMPORT FAILS, which the first version of this
 * missed. On 2026-09-11 "route chunk for TraplineDrill loaded without its export"
 * was reported twice from build CJm-McG3, while CJm-McG3 was the LIVE build and
 * its TraplineDrill chunk served a clean 200 with the export present. Nothing was
 * stale. The import had simply failed on the network, which is ordinary on a
 * phone, and the once-per-build guard then made it permanent: the reload landed on
 * the same build, the guard was spent, and the drill was unreachable until the
 * student reloaded by hand.
 *
 * So retry the import first, twice, with a short backoff. A transient fetch
 * failure almost always succeeds on the next try, and only after those fail is
 * "the chunk is gone" a reasonable conclusion worth spending the reload on.
 */
function lazyRoute<M extends Record<string, unknown>, K extends keyof M & string>(
  name: K,
  load: () => Promise<M>,
) {
  // Generic over the module so each route keeps its own props type. A plain
  // ComponentType<unknown> here compiles until a route takes props, then fails
  // with "Property 'module' does not exist on type 'IntrinsicAttributes'".
  return lazy(async () => {
    const mod = await loadWithRetry(load);
    const Component = mod?.[name];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the real
    // props type is restored by the M[K] cast on the return below.
    if (Component) return { default: Component as unknown as ComponentType<any> };
    if (claimChunkReload(`route:${name}`)) {
      window.location.reload();
      return new Promise<never>(() => {}); // the reload takes over
    }
    // TWO DIFFERENT FAILURES, AND THEY WERE REPORTED AS ONE.
    //
    // loadWithRetry returns undefined when the import failed every attempt, so
    // `mod` being undefined means THE CHUNK NEVER LOADED. Saying "loaded without
    // its export" about that is simply false, and it is the sentence that sent
    // three separate investigations after a shell/chunk mismatch, including the
    // comment in lib/build-id.ts calling it "BY DEFINITION a mismatch".
    //
    // They need different fixes. A chunk that will not load is a CDN or network
    // problem: a deploy purged it, or the connection failed three times. A chunk
    // that loads and lacks the export is a build problem, and would be the same
    // on a reload.
    throw new Error(
      mod === undefined
        ? `route chunk for ${name} failed to load after retries`
        : `route chunk for ${name} loaded without its export`,
    );
  }) as unknown as M[K];
}

const HomeNext = lazyRoute("HomeNext", () => import("./routes/HomeNext.js"));
const Drill = lazyRoute("Drill", () => import("./routes/Drill.js"));
const RepairSetRun = lazyRoute("RepairSetRun", () => import("./routes/RepairSet.js"));
const RepairSetDone = lazyRoute("RepairSetDone", () => import("./routes/RepairSet.js"));
const TraplineDrill = lazyRoute("TraplineDrill", () => import("./routes/TraplineDrill.js"));
const GrammarDrill = lazyRoute("GrammarDrill", () => import("./routes/GrammarDrill.js"));
const SliceDrill = lazyRoute("SliceDrill", () => import("./routes/SliceDrill.js"));
const DecoderDrill = lazyRoute("DecoderDrill", () => import("./routes/DecoderDrill.js"));
const RadarDrill = lazyRoute("RadarDrill", () => import("./routes/RadarDrill.js"));
const ScienceDrill = lazyRoute("ScienceDrill", () => import("./routes/ScienceDrill.js"));
const GraphGym = lazyRoute("GraphGym", () => import("./routes/GraphGym.js"));
const TestChoice = lazyRoute("TestChoice", () => import("./routes/TestChoice.js"));
const Plan = lazyRoute("Plan", () => import("./routes/Plan.js"));
const StemTrainer = lazyRoute("StemTrainer", () => import("./routes/StemTrainer.js"));
const FormulaTrainer = lazyRoute("FormulaTrainer", () => import("./routes/FormulaTrainer.js"));
const CalcRulesTrainer = lazyRoute("CalcRulesTrainer", () => import("./routes/CalcRulesTrainer.js"));
const CsaTraceTrainer = lazyRoute("CsaTraceTrainer", () => import("./routes/CsaTraceTrainer.js"));
const ApStatsTrainer = lazyRoute("ApStatsTrainer", () => import("./routes/ApStatsTrainer.js"));
const ScienceChoice = lazyRoute("ScienceChoice", () => import("./routes/ScienceChoice.js"));
const PaceTrainer = lazyRoute("PaceTrainer", () => import("./routes/PaceTrainer.js"));
const MentalMath = lazyRoute("MentalMath", () => import("./routes/MentalMath.js"));
const TrigValues = lazyRoute("TrigValues", () => import("./routes/TrigValues.js"));
const SessionSummary = lazyRoute("SessionSummary", () => import("./routes/SessionSummary.js"));
const Mistakes = lazyRoute("Mistakes", () => import("./routes/Mistakes.js"));
const MistakeReview = lazyRoute("MistakeReview", () => import("./routes/MistakeReview.js"));
const MasteryDeep = lazyRoute("MasteryDeep", () => import("./routes/MasteryDeep.js"));
const PackBrowser = lazyRoute("PackBrowser", () => import("./routes/PackBrowser.js"));
const Diagnostic = lazyRoute("Diagnostic", () => import("./routes/Diagnostic.js"));
const PracticeTestIntro = lazyRoute("PracticeTestIntro", () => import("./routes/PracticeTestIntro.js"));
const ActTestStart = lazyRoute("ActTestStart", () => import("./routes/ActTestStart.js"));
const ApCalcTestStart = lazyRoute("ApCalcTestStart", () => import("./routes/ApCalcTestStart.js"));
const PracticeTestRunner = lazyRoute("PracticeTestRunner", () => import("./routes/PracticeTestRunner.js"));
const PracticeTestReport = lazyRoute("PracticeTestReport", () => import("./routes/PracticeTestReport.js"));
const HiddenTest = lazyRoute("HiddenTest", () => import("./routes/HiddenTest.js"));
const Vyzrly = lazyRoute("Vyzrly", () => import("./routes/Vyzrly.js"));
const Onboarding = lazyRoute("Onboarding", () => import("./routes/Onboarding.js"));
const NotFound = lazyRoute("NotFound", () => import("./routes/NotFound.js"));
const Admin = lazyRoute("Admin", () => import("./routes/Admin.js"));
const Privacy = lazyRoute("Privacy", () => import("./routes/Privacy.js"));
const Terms = lazyRoute("Terms", () => import("./routes/Terms.js"));
const Support = lazyRoute("Support", () => import("./routes/Support.js"));
const Faq = lazyRoute("Faq", () => import("./routes/Faq.js"));
const Methodology = lazyRoute("Methodology", () => import("./routes/Methodology.js"));
const WhatsNew = lazyRoute("WhatsNew", () => import("./routes/WhatsNew.js"));
const Blog = lazyRoute("Blog", () => import("./routes/Blog.js"));
const BlogPost = lazyRoute("BlogPost", () => import("./routes/BlogPost.js"));
const FreePracticeTests = lazyRoute("FreePracticeTests", () => import("./routes/FreePracticeTests.js"));
const ExamDates = lazyRoute("ExamDates", () => import("./routes/ExamDates.js"));
const FreeActTests = lazyRoute("FreeActTests", () => import("./routes/FreeActTests.js"));
const SatWrongAnswerTraps = lazyRoute("SatWrongAnswerTraps", () => import("./routes/SatWrongAnswerTraps.js"));
const Patterns = lazyRoute("Patterns", () => import("./routes/Patterns.js"));
const PatternPage = lazyRoute("PatternPage", () => import("./routes/PatternPage.js"));
const PublicDemo = lazyRoute("PublicDemo", () => import("./routes/PublicDemo.js"));
const TrapFrame = lazyRoute("TrapFrame", () => import("./routes/TrapFrame.js"));
import { CanonicalLink } from "./components/CanonicalLink.js";
import { ScrollToTop } from "./components/ScrollToTop.js";
import { WakeLock } from "./components/WakeLock.js";
import { OfflineStatus } from "./components/OfflineStatus.js";
import { SyncErrorToast } from "./components/SyncErrorToast.js";
import { UpdateBanner } from "./components/UpdateBanner.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { loadSession } from "./lib/session.js";

/**
 * Route guard for signed-in users: if there's no session, bounce to /signin.
 * Onboarding is NOT enforced here anymore — that decision is made once at
 * sign-in (SignIn routes brand-new users to /app/welcome). Keeping the guard
 * to a pure auth check removes the re-onboarding bounce that came from
 * re-reading /me on every protected navigation.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = loadSession();
  const location = useLocation();
  if (!session) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

export function App() {
  // Route-level error boundary: a render crash is contained to the current
  // screen (shows the fallback + reports) and CLEARS when the user navigates,
  // because resetKey changes with the path. Without the keyed reset, the
  // app-level boundary in main.tsx catches the same crash but sticks until a
  // full reload. (Per-route boundaries on practice-test runner/report stay as
  // tighter inner boundaries.)
  const location = useLocation();
  return (
    <>
      <CanonicalLink pathname={location.pathname} />
      <ScrollToTop />
      <WakeLock />
      <OfflineStatus />
      <SyncErrorToast />
      <UpdateBanner />
      <ErrorBoundary resetKey={location.pathname} label={`route:${location.pathname}`}>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
      {/* Public marketing site */}
      <Route path="/" element={<Marketing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/methodology" element={<Methodology />} />
      <Route path="/whats-new" element={<WhatsNew />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blog/:slug/" element={<BlogPost />} />
      {/* Wrong-answer pattern reference. Canonical form carries NO trailing slash
          (see scripts/prerender.mjs); the slashed variants are registered so a
          typed or pasted URL still renders instead of falling through to 404. */}
      <Route path="/exam-dates" element={<ExamDates />} />
      <Route path="/exam-dates/" element={<ExamDates />} />
      <Route path="/free-practice-tests" element={<FreePracticeTests />} />
      <Route path="/free-practice-tests/" element={<FreePracticeTests />} />
      <Route path="/free-act-practice-tests" element={<FreeActTests />} />
      <Route path="/free-act-practice-tests/" element={<FreeActTests />} />
      <Route path="/sat-wrong-answer-traps" element={<SatWrongAnswerTraps />} />
      <Route path="/sat-wrong-answer-traps/" element={<SatWrongAnswerTraps />} />
      <Route path="/patterns" element={<Patterns />} />
      <Route path="/patterns/" element={<Patterns />} />
      <Route path="/patterns/:slug" element={<PatternPage />} />
      <Route path="/patterns/:slug/" element={<PatternPage />} />
      {/* Public no-signup demo: the conversion hook for cold traffic */}
      <Route path="/try" element={<PublicDemo />} />
      {/* Unlisted render-only frames for the short-form video pipeline */}
      <Route path="/_vf/:idx/:mode" element={<TrapFrame />} />

      {/* Auth */}
      <Route path="/signin" element={<SignIn />} />
      {/* The OAuth consent screen. Behind RequireAuth so a student who is not
          signed in is sent through sign-in first and lands back here, rather
          than being asked to approve something as nobody. */}
      <Route
        path="/app/authorize"
        element={
          <RequireAuth>
            <Authorize />
          </RequireAuth>
        }
      />
      <Route
        path="/app/welcome"
        element={
          <RequireAuth>
            <Onboarding />
          </RequireAuth>
        }
      />
      <Route
        path="/app/home"
        element={
          <RequireAuth>
            {/* HomeNext IS the default, since 2026-09-05 (flags.ts homeNext:
                true). ?ui=off returns a student to the old Home, which is the
                only reason Home.tsx is still in the tree.
                This comment used to say the flag was off for everyone until
                ?ui=next, which was true when it was written and has been wrong
                since. It cost a cold-start entry point being built into the
                component nobody sees. If you change the flag, change this. */}
            {flag("homeNext") ? <HomeNext /> : <Home />}
          </RequireAuth>
        }
      />
      <Route
        path="/app/admin"
        element={
          <RequireAuth>
            <Admin />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/trapline"
        element={
          <RequireAuth>
            <TraplineDrill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/grammar"
        element={
          <RequireAuth>
            <GrammarDrill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/slice"
        element={
          <RequireAuth>
            <SliceDrill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/prompt"
        element={
          <RequireAuth>
            <DecoderDrill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/science"
        element={
          <RequireAuth>
            <ScienceDrill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/act_science"
        element={
          <RequireAuth>
            <ScienceDrill module="act_science" />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/rubric"
        element={
          <RequireAuth>
            <RadarDrill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/graph-gym"
        element={
          <RequireAuth>
            <GraphGym />
          </RequireAuth>
        }
      />
      <Route
        path="/app/plan"
        element={
          <RequireAuth>
            <Plan />
          </RequireAuth>
        }
      />
      <Route
        path="/app/test-choice"
        element={
          <RequireAuth>
            <TestChoice />
          </RequireAuth>
        }
      />
      <Route
        path="/app/act-stems"
        element={
          <RequireAuth>
            <StemTrainer />
          </RequireAuth>
        }
      />
      <Route
        path="/app/act-formulas"
        element={
          <RequireAuth>
            <FormulaTrainer />
          </RequireAuth>
        }
      />
      <Route
        path="/app/calc-rules"
        element={
          <RequireAuth>
            <CalcRulesTrainer />
          </RequireAuth>
        }
      />
      {/* BC is its own route, not a filter on the AB one. The two banks never
          mix: a student sitting AB must not meet a ratio test. */}
      <Route
        path="/app/calc-bc-rules"
        element={
          <RequireAuth>
            <CalcRulesTrainer course="bc" />
          </RequireAuth>
        }
      />
      <Route
        path="/app/csa-trace"
        element={
          <RequireAuth>
            <CsaTraceTrainer />
          </RequireAuth>
        }
      />
      {/* "inference-calculations", NOT "ap-statistics". The route name is the
          first place an overclaim would creep in, and interpretation is 25-35%
          of the real exam's multiple-choice skills. */}
      <Route
        path="/app/inference-calculations"
        element={
          <RequireAuth>
            <ApStatsTrainer />
          </RequireAuth>
        }
      />
      <Route
        path="/app/act-science"
        element={
          <RequireAuth>
            <ScienceChoice />
          </RequireAuth>
        }
      />
      <Route
        path="/app/pace"
        element={
          <RequireAuth>
            <PaceTrainer />
          </RequireAuth>
        }
      />
      <Route
        path="/app/mental-math"
        element={
          <RequireAuth>
            <MentalMath />
          </RequireAuth>
        }
      />
      <Route
        path="/app/trig-values"
        element={
          <RequireAuth>
            <TrigValues />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/:module"
        element={
          <RequireAuth>
            <Drill />
          </RequireAuth>
        }
      />
      <Route
        path="/app/drill/demo"
        element={<Navigate to="/app/drill/trapline" replace />}
      />
      <Route
        path="/app/repair/:id"
        element={
          <RequireAuth>
            <RepairSetRun />
          </RequireAuth>
        }
      />
      <Route
        path="/app/repair/:id/done"
        element={
          <RequireAuth>
            <RepairSetDone />
          </RequireAuth>
        }
      />
      <Route
        path="/app/session/summary"
        element={
          <RequireAuth>
            <SessionSummary />
          </RequireAuth>
        }
      />
      <Route
        path="/app/mistakes"
        element={
          <RequireAuth>
            <MistakeReview />
          </RequireAuth>
        }
      />
      <Route
        path="/app/mistakes/legacy"
        element={
          <RequireAuth>
            <Mistakes />
          </RequireAuth>
        }
      />
      <Route
        path="/app/mastery"
        element={
          <RequireAuth>
            <MasteryDeep />
          </RequireAuth>
        }
      />
      <Route
        path="/app/packs"
        element={
          <RequireAuth>
            <PackBrowser />
          </RequireAuth>
        }
      />
      <Route
        path="/app/vyzrly"
        element={
          <RequireAuth>
            <Vyzrly />
          </RequireAuth>
        }
      />
      <Route
        path="/app/diagnostic"
        element={
          <RequireAuth>
            <Diagnostic />
          </RequireAuth>
        }
      />
      <Route
        path="/app/practice-test"
        element={
          <RequireAuth>
            <PracticeTestIntro />
          </RequireAuth>
        }
      />
      <Route
        path="/app/act-test"
        element={
          <RequireAuth>
            <ActTestStart />
          </RequireAuth>
        }
      />
      <Route
        path="/app/ap-calculus-test"
        element={
          <RequireAuth>
            <ApCalcTestStart />
          </RequireAuth>
        }
      />
      <Route
        path="/app/practice-test/run/:id"
        element={
          <RequireAuth>
            <ErrorBoundary label="practice-test-runner">
              <PracticeTestRunner />
            </ErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/app/practice-test/report/:id"
        element={
          <RequireAuth>
            <ErrorBoundary label="practice-test-report">
              <PracticeTestReport />
            </ErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/app/hidden-test"
        element={
          <RequireAuth>
            <HiddenTest />
          </RequireAuth>
        }
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}

/** Minimal hold while a lazy route chunk loads (usually a few ms on cache). */
function RouteFallback() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--fg-3)",
        fontFamily: "var(--font-mono)",
        fontSize: 13,
      }}
    >
      Loading…
    </div>
  );
}
